<template>
  <div>
    <FreePageLayout>
      <template #header>
        <FreeHeader class="p-4" is-centered>
          <template #leftAction>
            <FreeBackButton @click="onBack" />
          </template>
          <template #default>
            <h2
              class="text-text-neutral-primary text-center text-lg font-semibold"
            >
              {{ $t('quickBooking.title') }}
            </h2>
          </template>
        </FreeHeader>
      </template>
      <template #default>
        <div
          v-if="isInitializing || isSubmitting"
          class="fixed left-0 top-0 z-50 h-screen w-screen"
        >
          <FullScreenLoader />
        </div>

        <main
          v-else-if="bookingStore.offer"
          class="flex grow flex-col gap-4 pb-36"
        >
          <SmartRoute
            v-if="isSmartRoute"
            @onLabelClick="bottomSheet.show(smartRouteBottomSheetName)"
          />

          <FlightDetails
            v-if="bookingStore.dict"
            class="-mx-4 [&>*]:!mb-0"
            :dict="bookingStore.dict"
            :flights="bookingStore.mappedFlights"
            :city-mismatches="bookingStore.offer?.$meta.city_mismatch"
            :count-passengers="totalPassengersCount"
            :is-youth="isYouthOffer"
          />

          <section
            v-if="hasFareGroups"
            class="space-y-3"
          >
            <h3 class="text-text-neutral-tertiary text-sm font-medium">
              {{ $t('quickBooking.fare.title') }}
            </h3>

            <!-- Multi-direction (RT) — per-leg card + bottom-sheet picker.
                 Mirrors the classic FareFamilyPage UX so users get the same
                 per-segment fare-comparison view inside QuickBooking. -->
            <div
              v-if="Object.keys(fareFamily.fareGroups.value).length > 1"
              class="space-y-3"
            >
              <FareSegmentCard
                v-for="(groupChoices, groupKey, groupIdx) in fareFamily
                  .fareGroups.value"
                :key="groupKey"
                :group-key="groupKey as string"
                :group-idx="groupIdx"
                :flight-group="
                  fareFamily.flightGroups.value[groupKey as string]
                "
                :choices="groupChoices"
                :selected-fare="
                  fareFamily.selectedFares[groupKey as string] ?? null
                "
                :is-missing="
                  segmentGroupsMissingFares.includes(groupKey as string)
                "
                @onOpenFareSelect="onOpenSegmentFareSelect"
              />
            </div>

            <!-- Single-direction (OW) — horizontal-scroll card row. -->
            <div
              v-else
              ref="fareCarouselRef"
              :class="
                totalFareCardCount === 1
                  ? 'flex flex-col gap-3'
                  : 'no-scrollbar -mx-4 flex gap-3 overflow-x-auto px-4 pb-2'
              "
            >
              <div
                v-for="(groupChoices, groupKey) in fareFamily.fareGroups.value"
                :key="groupKey"
                :class="
                  totalFareCardCount === 1
                    ? 'flex flex-col gap-3'
                    : 'flex gap-3'
                "
              >
                <FareCard
                  v-for="(choice, idx) in groupChoices"
                  :key="choice.id"
                  :class="
                    totalFareCardCount === 1
                      ? 'w-full'
                      : 'w-[260px] flex-shrink-0'
                  "
                  :data-fare-id="choice.id"
                  :fare-idx="idx + 1"
                  :choice="choice"
                  :provider-class="providerClass"
                  :connect-type="bookingStore.offer?.$meta?.connect_type"
                  :is-domestic="bookingStore.offer?.$meta?.is_domestic"
                  :search-query="bookingStore.offer?.$meta?.search_query"
                  :is-base-fare="false"
                  :selected-id="
                    fareFamily.selectedFares[groupKey as string]?.id ?? null
                  "
                  @onFareSelect="onFareCardSelect(groupKey as string, choice)"
                />
              </div>
            </div>
          </section>

          <section
            class="space-y-2"
          >
            <div class="flex items-center justify-between">
              <h3 class="text-text-neutral-tertiary text-sm font-medium">
                {{
                  passengerKeys.length > 1
                    ? $t('quickBooking.passengerSection.titleMulti')
                    : $t('quickBooking.passengerSection.title')
                }}
              </h3>
              <span
                v-if="passengerKeys.length > 1"
                class="text-text-neutral-tertiary text-xs font-medium"
              >
                {{
                  $t('quickBooking.passengerSection.counter', {
                    filled: filledPassengersCount,
                    total: passengerKeys.length,
                  })
                }}
              </span>
            </div>
            <div
              class="bg-background-neutral-secondary overflow-hidden rounded-2xl"
            >
              <div
                v-for="(passengerKey, idx) in passengerKeys"
                :key="passengerKey"
                :class="[
                  'quick-booking-passenger-row',
                  getPassengerValidationError(passengerKey) &&
                    'quick-booking-passenger-row--invalid',
                ]"
              >
                <button
                  type="button"
                  class="flex w-full items-center justify-between gap-2 p-4 text-left"
                  @click="openPassengerSheet(passengerKey)"
                >
                  <div class="min-w-0 flex-1">
                    <p class="text-text-neutral-primary truncate font-semibold">
                      {{ getPassengerTitle(passengerKey) }}
                    </p>
                    <p
                      class="truncate text-sm"
                      :class="
                        passengersMissingData.includes(passengerKey)
                          ? 'text-text-negative-primary'
                          : 'text-text-neutral-tertiary'
                      "
                    >
                      {{
                        passengersMissingData.includes(passengerKey)
                          ? $t('quickBooking.passengerSection.missingData')
                          : getPassengerDescription(passengerKey)
                      }}
                    </p>
                  </div>
                  <FreeButton
                    size="sm"
                    type="button"
                    :class="[
                      'w-32 shrink-0',
                      isPassengerFilledFor(passengerKey)
                        ? 'bg-situational-lightgrey text-text-neutral-primary'
                        : 'green-main-button',
                    ]"
                    style="--free-btn-border-radius: 0.75rem"
                  >
                    {{
                      isPassengerFilledFor(passengerKey)
                        ? $t('quickBooking.passengerSection.edit')
                        : $t('quickBooking.passengerSection.add')
                    }}
                  </FreeButton>
                </button>
                <p
                  v-if="getPassengerValidationError(passengerKey)"
                  class="text-text-negative-primary px-4 pb-3 text-xs"
                >
                  {{ getPassengerValidationError(passengerKey) }}
                </p>
                <div class="border-situational-lightgrey mx-4 border-t" />
              </div>
              <div
                class="contacts-form-wrapper [&>div]:!bg-transparent [&>div]:!p-4"
              >
                <ContactsForm />
              </div>
            </div>
          </section>

          <section
            v-if="isSavingPassenger"
            class="bg-background-neutral-secondary flex items-center justify-center rounded-2xl p-6"
          >
            <div class="shimmer h-12 w-full rounded-xl" />
          </section>
          <section
            v-else-if="hasAnyExtras"
            class="bg-background-neutral-secondary overflow-hidden rounded-2xl"
          >
            <button
              type="button"
              class="flex w-full items-center justify-between px-4 py-4 text-left"
              @click="isExtrasOpen = !isExtrasOpen"
            >
              <div class="flex items-center gap-3">
                <span
                  class="bg-background-positive-primary flex h-9 w-9 items-center justify-center rounded-xl text-white"
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      d="M10 2L11.6 8.4L18 10L11.6 11.6L10 18L8.4 11.6L2 10L8.4 8.4L10 2Z"
                    />
                  </svg>
                </span>
                <div class="min-w-0">
                  <p
                    class="text-text-neutral-primary text-left text-sm font-semibold"
                  >
                    {{ $t('quickBooking.extras.title') }}
                  </p>
                  <p class="text-text-neutral-tertiary text-left text-xs">
                    {{ $t('quickBooking.extras.description') }}
                  </p>
                </div>
              </div>
              <div class="flex items-center gap-2">
                <span
                  v-if="extrasSelectedCount > 0"
                  class="bg-background-positive-primary flex h-5 w-5 items-center justify-center rounded-full text-xs font-semibold text-white"
                >
                  {{ extrasSelectedCount }}
                </span>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  class="text-text-neutral-quaternary transition-transform duration-300"
                  :class="{ 'rotate-180': isExtrasOpen }"
                >
                  <path
                    d="M5 7.5L10 12.5L15 7.5"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </div>
            </button>
            <Transition name="extras-collapse">
              <div v-show="isExtrasOpen" class="space-y-3 px-4 pb-4">
                <!-- Baggage/seat availability is gated by the post-save
                     /airflow/ancillaries + /airflow/seatmap responses, which
                     only run after all passenger data is filled. Replace the
                     two buttons with an info banner until then so the section
                     is not empty / does not advertise services that cannot
                     yet be opened. -->
                <div
                  v-if="!modelIsPassengerFilled"
                  class="bg-background-info-primary/10 flex items-start gap-3 rounded-2xl p-4"
                >
                  <InfoFilledIcon
                    class="text-background-info-primary mt-0.5 h-5 w-5 shrink-0"
                  />
                  <div class="min-w-0 space-y-1">
                    <p class="text-text-neutral-primary text-sm font-semibold">
                      {{ $t('quickBooking.extras.passengersRequiredTitle') }}
                    </p>
                    <p class="text-text-neutral-secondary text-sm">
                      {{
                        $t('quickBooking.extras.passengersRequiredDescription')
                      }}
                    </p>
                  </div>
                </div>
                <SeatMapButton
                  v-if="modelIsPassengerFilled && isSeatMapAvailable"
                  class="border-situational-lightgrey border"
                  :offer-id="offerId"
                  :is-loading="checkSeatMapQuery.isLoading.value"
                  :seat-map-state-by-segment="bookingStore.state.seatMap"
                  :mapped-flights="bookingStore.mappedFlights"
                  :passengers-data="bookingStore.state.passengers"
                  @onProceedToSeatMap="onProceedToSeatMap"
                />
                <AncillaryBaggagePageOpenButton
                  v-if="
                    modelIsPassengerFilled &&
                    (ancillariesStore.isAncillariesLoading ||
                      ancillariesStore.isBaggageAvailable)
                  "
                  class="border-situational-lightgrey border"
                  :is-loading="ancillariesStore.isAncillariesLoading"
                  :total-price="
                    ancillariesStore.passengersWithBaggage.totalPrice
                  "
                  :has-selected-baggage="
                    Boolean(
                      ancillariesStore.passengersWithBaggage.data
                        ?.hasSelectedBaggage,
                    )
                  "
                  :is-baggage-available="ancillariesStore.isBaggageAvailable"
                  @onProceedToBaggages="onProceedToBaggages"
                />
                <PackageList
                  v-if="products.packages.value.length > 0"
                  :packages="products.packages.value"
                  :package-info="products.packageInfo.value"
                  disable-auto-scroll
                  @onSelect="onSelectPackage"
                  @onUnselect="onUnselectPackage"
                />
                <div
                  v-for="product in products.visibleProducts.value"
                  :key="product.id"
                  class="border-situational-lightgrey overflow-hidden rounded-2xl border [&>div>div]:!rounded-none"
                >
                  <ProductCard
                    :product="product"
                    :products-state="products.state.value"
                    :package-info="products.packageInfo.value"
                    @onProductAdd="products.addProductSeparately"
                    @onProductRemove="products.removeProduct"
                    @onProductDataUpdate="products.setProductData"
                  />
                </div>
              </div>
            </Transition>
          </section>

          <AgreementSection
            :public-offer-bs-name="publicOfferBsName"
            :exchange-return-bs-name="exchangeReturnBsName"
            :is-agreed="isAgreed"
            @onAgree="setAgreement"
          />

          <FreeAlert
            ref="invalidAlertRef"
            class="sticky bottom-32 mx-auto w-fit"
            :duration="8000"
          >
            <template #icon>
              <img
                src="@/assets/images/red-warning.svg"
                width="24"
                height="24"
                alt=""
              />
            </template>
            <template #default>
              <p class="text-text-neutral-primary">{{ submitErrorMessage }}</p>
            </template>
          </FreeAlert>
        </main>

        <PriceAndButtonCard
          v-if="bookingStore.offer && !isInitializing"
          source-page="quickBooking"
          :price-amount="priceBreakdown.totalPrice"
          :button-title="$t('contacts.book')"
          :is-loading="isSubmitting"
          @onButtonAction="onPayClick"
        >
          <PriceDetails class="-mt-5" :price-breakdown="priceBreakdown" />
        </PriceAndButtonCard>
      </template>
    </FreePageLayout>

    <!-- PassengersSection is mounted at page level (not inside a bottom
         sheet) so its `openSavedList(key)` ref method works on the very
         first "Add" tap. The inline header + passenger-block list it
         renders inside its root <section> are hidden via CSS — only its
         nested bottom-sheets (saved-list, form, etc.) are user-visible
         when triggered. -->
    <div
      v-if="passengers.state.value && bookingStore.passengersSchema"
      class="quick-booking-passengers-host"
    >
      <PassengersSection
        ref="passengersSectionRef"
        :is-domestic="bookingStore.offer?.$meta.is_domestic"
        :schema="bookingStore.passengersSchema.passengers.schema"
        :passenger-keys="passengers.keys.value"
        :passengers="passengers.state.value"
        :provider-class="bookingStore.offer?.$meta.provider_class"
        :departure-date="bookingStore.mappedFlights[0]?.segments?.[0]?.dep?.at"
        disable-auto-open
        @onPassengerUpdate="onPassengerUpdate"
      />
    </div>

    <SpecialSymbolsWarningModal :name="specialSymbolsWarningModalName" />

    <BookingWarningModal
      name="booking-warning-modal"
      @onConfirm="onWarningConfirm"
    />
    <BookingErrorModal :name="modalNames.initError">
      <template #action>
        <FreeButton
          type="button"
          class="gray-button w-full"
          @click="handleNewSearchStart"
        >
          {{ $t('booking.startNewSearch') }}
        </FreeButton>
      </template>
    </BookingErrorModal>
    <BookingErrorModal name="booking-expired-error-modal">
      <template #action>
        <FreeButton
          type="button"
          class="gray-button w-full"
          @click="handleNewSearchStart"
        >
          {{ $t('booking.startNewSearch') }}
        </FreeButton>
      </template>
    </BookingErrorModal>
    <DoubleBookingModal
      name="booking-double-booking-modal"
      @onProceedToExistingBooking="goToExistingBooking"
      @onCancelPrevBooking="onCancelPrevBooking"
    />
    <BookingInitErrorModal name="booking-init-error-modal" />

    <PublicOfferBottomSheet
      :name="publicOfferBsName"
      :fetch-public-offer="publicOfferApi.fetchPublicOffer"
    />
    <ExchangeRefundRulesBottomSheet
      :name="exchangeReturnBsName"
      :offer-id="offerId"
    />

    <FareFamilyBottomSheet @onFareSelect="onSegmentFareSelect" />

    <SmartRouteBottomSheet :name="smartRouteBottomSheetName" />
  </div>
</template>

<script setup lang="ts">
  import { computed, nextTick, onMounted, ref, watch } from 'vue';
  import { useRouter } from 'vue-router';
  import { useI18n } from 'vue-i18n';
  import { useVuelidate } from '@vuelidate/core';
  import { createStorage } from '@aviata/storage';
  import {
    createPassengersData,
    type Passenger,
    type PassengerKey,
  } from '@aviata/booking-data';
  import {
    bookingPassengerEntity,
    FreeAlert,
    FreeBackButton,
    FreeButton,
    FreeHeader,
    FreePageLayout,
    FullScreenLoader,
    PublicOfferBottomSheet,
  } from 'freedom-shared';
  import { controllerModel, hashCodeLib } from 'shared';

  import { SeatMapButton } from '@/pages/booking/products/seat-map';
  import PriceDetails from '@/pages/booking/products/products/PriceDetails.vue';
  import { SmartRoute, SmartRouteBottomSheet } from '@/pages/offer-details/ui';

  import {
    PassengersSection,
    SpecialSymbolsWarningModal,
  } from '@/widgets/passengers-form';
  import { ContactsForm } from '@/widgets/contacts-form';
  import {
    AgreementSection,
    BookingWarningModal,
    DoubleBookingModal,
    BookingInitErrorModal,
  } from '@/widgets/booking-modals';
  import { AncillaryBaggagePageOpenButton } from '@/widgets/ancillary-baggage';
  import { ProductCard } from '@/widgets/product-card';

  import {
    FareCard,
    FareFamilyBottomSheet,
    FareSegmentCard,
    useFareFamilyBottomSheet,
    type FareFamilyChoiceSpec,
  } from '@/features/fare-family';
  import { PriceAndButtonCard } from '@/features/price-and-button-card';

  import { PackageList } from '@/entities/product-packages';
  import { publicOfferApi } from '@/entities/public-offer';
  import { ExchangeRefundRulesBottomSheet } from '@/entities/exchange-refund-rules-bottom-sheet';
  import { isSmartOffer } from '@/entities/offer';
  import { BookingErrorModal } from '@/entities/booking-error-modal';

  import { amplitudeEventsLib } from '@/shared/lib';

  import InfoFilledIcon from '@/assets/images/info-filled-icon.svg?component';

  import { FlightDetails } from '../details/passengers-data/ui';
  import { useQuickBooking } from './model/use-quick-booking';

  const props = defineProps<{
    offerId: string;
    resultId: string;
  }>();

  const router = useRouter();
  const { t } = useI18n();

  const publicOfferBsName = 'public-offer-bottom-sheet';
  const exchangeReturnBsName = 'exchange-return-rules-bottom-sheet';
  const smartRouteBottomSheetName = 'smart-route-bottom-sheet';
  const specialSymbolsWarningModalName = 'special-symbols-warning-modal';

  const bottomSheet = controllerModel.useBottomSheet();

  const passengersData = createPassengersData(
    createStorage(sessionStorage),
    'free-avia:passengers',
  );

  const validations = useVuelidate();
  const invalidAlertRef = ref<InstanceType<typeof FreeAlert>>();
  const passengersSectionRef = ref<InstanceType<typeof PassengersSection>>();
  const fareCarouselRef = ref<HTMLElement | null>(null);

  const isAgreed = ref(true);
  const isExtrasOpen = ref(true);

  const setAgreement = (value: boolean) => {
    isAgreed.value = value;
  };

  const {
    details,
    bookingStore,
    ancillariesStore,
    products,
    fareFamily,
    passengers,
    checkSeatMapQuery,
    extrasFetchEnabled,
    priceBreakdown,
    isSubmitting,
    submitErrorMessage,
    providerClass,
    isPassengerFilled: modelIsPassengerFilled,
    isSavingPassenger,
    onSubmit,
    savePassengersForExtras,
    preselectFirstPackage,
    onWarningConfirm: onWarningConfirmFromModel,
    onCancelPrevBooking: onCancelPrevBookingFromModel,
    handleNewSearchStart,
    goToExistingBooking: goToExistingBookingFromModel,
    updateProductsStateAtBookingStore,
    modalNames,
  } = useQuickBooking({
    offerId: props.offerId,
    resultId: props.resultId,
    vuelidate: validations,
  });

  // Captured after the deferred products fetch resolves so it can be included
  // in the page-open amplitude payload.
  const preselectedPackageId = ref<string | null>(null);

  // Tracks completion of the products init so the page-open analytics watcher
  // waits for the preselect outcome instead of firing with a stale `null`
  // while /airflow/products is still in flight.
  const isProductsInitDone = ref(false);

  // products.init() MUST be called during setup so the underlying useQuery
  // registers against the active component instance (vue-query resolves its
  // QueryClient via `inject()`, which only works in setup). The actual HTTP
  // call is gated by `extrasFetchEnabled` inside getProducts — flipped to
  // true by `initProductsAfterFareReady()` once the initial fare-change has
  // settled. `productsQuery.suspense()` stays pending until the gate opens,
  // so the .then below runs at the right time.
  //
  // skipDefaultSelection: we override the backend's `select_by_default` and
  // always pre-pick the FIRST visible package for QuickBooking (per product
  // decision). The default-selection path is skipped here and replaced by
  // `preselectFirstPackage()` so the QB default is deterministic and
  // independent of backend ordering quirks.
  products
    .init({ skipDefaultSelection: true })
    .then(() => {
      preselectedPackageId.value = preselectFirstPackage();
    })
    .catch(() => {
      // Errors are captured in getProducts' onError → Sentry.
    })
    .finally(() => {
      isProductsInitDone.value = true;
    });

  let hasStartedProductsInit = false;
  const initProductsAfterFareReady = () => {
    if (hasStartedProductsInit) {
      return;
    }
    hasStartedProductsInit = true;
    extrasFetchEnabled.value = true;
  };

  const isInitializing = computed(() => {
    // When details has resolved with an error (offer_expired / not_found),
    // bookingStore.offer is still null but the error modal has been shown.
    // Don't keep the full-screen loader on top of it — that hides the modal.
    if (details.isInitFailed.value) {
      return false;
    }
    return details.isLoading.value || !bookingStore.offer;
  });

  const isSeatMapAvailable = computed(
    () =>
      checkSeatMapQuery.isLoading.value ||
      Boolean(checkSeatMapQuery.data.value?.available),
  );

  const hasFareGroups = computed(
    () => Object.keys(fareFamily.fareGroups.value).length > 0,
  );

  const totalFareCardCount = computed(() =>
    Object.values(fareFamily.fareGroups.value).reduce(
      (acc, choices) => acc + choices.length,
      0,
    ),
  );

  const hasAnyExtras = computed(
    () =>
      isSeatMapAvailable.value ||
      ancillariesStore.isAncillariesLoading ||
      ancillariesStore.isBaggageAvailable ||
      products.packages.value.length > 0 ||
      products.visibleProducts.value.length > 0,
  );

  const extrasSelectedCount = computed(() => {
    let count = 0;
    if (bookingStore.state.seatMap) {
      count += 1;
    }
    if (ancillariesStore.passengersWithBaggage.data?.hasSelectedBaggage) {
      count += 1;
    }
    if (products.packageInfo.value?.id) {
      count += 1;
    }
    count += products.selectedProducts.value?.length ?? 0;
    return count;
  });

  /** Stable, schema-derived passenger key list ("ADT-0", "CHD-0", ...).
   *  Falls back to the runtime state until the schema lands, so the loop
   *  renders one row per pax from the very first paint. */
  const passengerKeys = computed<PassengerKey[]>(() => {
    const schema = bookingStore.passengersSchema?.passengers?.schema;
    if (schema) {
      return Object.keys(schema) as PassengerKey[];
    }
    const state = passengers.state.value;
    return state ? (Object.keys(state) as PassengerKey[]) : [];
  });

  /** Per-key passenger record. We always read from `passengers.state.value`
   *  rather than capturing it once so the template stays reactive while the
   *  user fills the bottom-sheet. */
  const getPassenger = (key: PassengerKey) =>
    passengers.state.value?.[key] ?? null;

  const isPassengerFilledFor = (key: PassengerKey): boolean => {
    const p = getPassenger(key);
    return Boolean(p?.firstName) && Boolean(p?.lastName);
  };

  /** Per-pax-type ordinal (1-based) computed from the position of the key
   *  among same-type siblings. So `ADT-0` → 1, `ADT-1` → 2, `CHD-0` → 1, etc. */
  const getPassengerOrdinal = (key: PassengerKey): number => {
    const [type] = String(key).split('-');
    const siblings = passengerKeys.value.filter(k =>
      String(k).startsWith(`${type}-`),
    );
    return siblings.indexOf(key) + 1;
  };

  /** Derived from the offer's search_query hash — drives the FlightDetails
   *  cabin-class label so a Youth offer reads "Молодёжный" instead of being
   *  collapsed to "Эконом". Mirrors how PassengersDataPage extracts isYouth. */
  const isYouthOffer = computed<boolean>(() => {
    const query = bookingStore.offer?.$meta?.search_query;
    if (typeof query !== 'string') {
      return false;
    }
    return Boolean(hashCodeLib.getSearchParams(query).isYouth);
  });

  /** Smart-route banner is shown for stitched/virtual-interline offers
   *  (connect_type VI / OWC / ML). Mirrors how FareFamilyPage decides whether
   *  to surface the same disclaimer above the fare cards. */
  const isSmartRoute = computed<boolean>(() =>
    bookingStore.offer ? isSmartOffer(bookingStore.offer) : false,
  );

  /** Youth offers come down the wire with an `ADT-*` passenger key (the
   *  backend reuses the adult slot, just with a 12-24 age bracket), so the
   *  raw key alone makes every row read as "Взрослый" on a youth fare.
   *  Promote ADT → YTH whenever the offer is flagged as youth so the label
   *  + description match the tariff the user actually picked. */
  const getEffectivePaxType = (
    key: PassengerKey,
  ): 'ADT' | 'CHD' | 'INF' | 'YTH' => {
    const type = String(key).split('-')[0] as 'ADT' | 'CHD' | 'INF' | 'YTH';
    if (type === 'ADT' && isYouthOffer.value) {
      return 'YTH';
    }
    return type;
  };

  const getPassengerTitle = (key: PassengerKey): string => {
    const p = getPassenger(key);
    if (p && (p.firstName || p.lastName)) {
      const fullName = [p.lastName, p.firstName]
        .filter(Boolean)
        .join(' ')
        .trim();
      if (fullName) {
        return fullName;
      }
    }
    const labelKey = `quickBooking.passengerSection.paxLabel.${getEffectivePaxType(key)}`;
    return t(labelKey, { idx: getPassengerOrdinal(key) });
  };

  const getPassengerDescription = (key: PassengerKey): string => {
    const p = getPassenger(key);
    if (p && (p.firstName || p.lastName)) {
      const docNumber = p.document?.number;
      if (docNumber) {
        const docType = (p.document as { type?: string }).type ?? '';
        return bookingPassengerEntity.lib.getPaxDocText(docType, docNumber);
      }
      return t('quickBooking.passengerSection.documentPlaceholder');
    }
    // Age ranges come straight from the offer's passenger schema so the
    // descriptions match what the backend will validate against — the bounds
    // differ for domestic vs international and between YTH/CHD fare rules,
    // so a hardcoded "12 to 23 years" diverged from prod. The +1 on max
    // mirrors PassengerCard: the schema bound is inclusive while the
    // localized copy reads as "up to but not including".
    const ageBetween =
      bookingStore.passengersSchema?.passengers?.schema?.[key]?.validators
        ?.age_between;
    return t(
      `quickBooking.passengerSection.paxDescription.${getEffectivePaxType(key)}`,
      {
        min: ageBetween?.min ?? 0,
        max: ageBetween?.max != null ? ageBetween.max + 1 : undefined,
      },
    );
  };

  const filledPassengersCount = computed(
    () => passengerKeys.value.filter(isPassengerFilledFor).length,
  );

  const totalPassengersCount = computed(() => passengerKeys.value.length || 1);

  /** Key of the passenger that was tapped — used to scroll/scope the bottom
   *  sheet content once we wire that up. For now the sheet still shows all
   *  passengers, but we capture the intent for later UX iteration. */
  const selectedPassengerKey = ref<PassengerKey | null>(null);

  /** Passenger keys that triggered the "fill passenger" validation error on
   *  the last submit attempt. Drives the per-row red ring so the user can see
   *  which passenger blocks are missing data — cleared as each gets filled. */
  const passengersMissingData = ref<PassengerKey[]>([]);

  const initPassengersFromSchema = () => {
    if (!bookingStore.passengersSchema) {
      return;
    }
    const passengersKeys = Object.keys(
      bookingStore.passengersSchema.passengers.schema,
    ) as PassengerKey[];
    const { passengers: initialPassengers, passengersKeys: sortedKeys } =
      passengersData.init(passengersKeys);
    passengers.init({
      keys: sortedKeys,
      initialState: initialPassengers,
      schema: bookingStore.passengersSchema.passengers.schema,
    });
  };

  /** Walk the nested vuelidate tree (PassengersSection wraps each pax block
   *  in its own collector, so the child registered with `$registerAs: key`
   *  may not be a direct property of the root) and `$touch()` the matching
   *  pax. Returns true when the pax validation node was found.
   *  Touching one pax in isolation keeps the red-text feedback scoped to
   *  the row the user actually filled — a global `$touch()` would light up
   *  paxes the user hasn't gotten to yet. */
  const touchPassengerValidation = (
    node: unknown,
    name: PassengerKey,
  ): boolean => {
    if (!node || typeof node !== 'object') {
      return false;
    }
    const record = node as Record<string, unknown>;
    const direct = record[name as string];
    if (
      direct &&
      typeof direct === 'object' &&
      typeof (direct as { $touch?: unknown }).$touch === 'function'
    ) {
      (direct as { $touch: () => void }).$touch();
      return true;
    }
    for (const childKey of Object.keys(record)) {
      if (childKey.startsWith('$')) {
        continue;
      }
      if (touchPassengerValidation(record[childKey], name)) {
        return true;
      }
    }
    return false;
  };

  /** Per-pax validation message extracted from the parent vuelidate's
   *  `$errors` tree. Each `MultiplePassengerBlock` registers its child
   *  validation with `$registerAs: passengerKey`, so any failing rule on
   *  that pax shows up here with `passengerKey` somewhere in
   *  `$propertyPath` (e.g. "ADT-0.dob"). Returns '' when the pax has no
   *  errors or the pax hasn't been touched yet — vuelidate hides
   *  `$errors` entries until `$dirty`. */
  const getPassengerValidationError = (key: PassengerKey): string => {
    const errors =
      (
        validations.value as unknown as {
          $errors?: Array<{ $propertyPath?: string; $message?: unknown }>;
        }
      )?.$errors ?? [];
    const paxError = errors.find(e => {
      const path = typeof e.$propertyPath === 'string' ? e.$propertyPath : '';
      return path
        .split(/[.[\]]/)
        .filter(Boolean)
        .includes(key as string);
    });
    if (!paxError) {
      return '';
    }
    const msg = paxError.$message;
    return typeof msg === 'string' ? msg : String(msg ?? '');
  };

  const onPassengerUpdate = async (payload: {
    key: PassengerKey;
    passenger: Passenger;
  }) => {
    passengers.onUpdate(payload);
    if (passengers.state.value) {
      passengersData.save(passengers.state.value);
    }
    // Clear the red ring on this passenger row once the user fills it in.
    if (isPassengerFilledFor(payload.key)) {
      passengersMissingData.value = passengersMissingData.value.filter(
        key => key !== payload.key,
      );
    }
    // Each saved-list/form submission lands here. Once every required pax
    // has data filled, fire the analytics + kick the extras-save call.
    // `savePassengersForExtras` is idempotent on identical payloads, safe
    // to call on every update.
    if (!modelIsPassengerFilled.value) {
      return;
    }

    // Surface this pax's age/format errors inline (red label below the row)
    // BEFORE firing the extras pre-save. Without this guard an age-invalid
    // passenger (e.g. an ADT slot filled with a child's DOB) would
    // round-trip to the backend and surface as the QB_PASSENGER_ERROR_MODAL
    // — the classic PassengersDataPage catches it client-side via vuelidate
    // and shows the same red-border + alert pattern.
    touchPassengerValidation(validations.value, payload.key);
    await nextTick();

    const hasAnyPaxError = passengerKeys.value.some(
      k => getPassengerValidationError(k) !== '',
    );
    if (hasAnyPaxError) {
      return;
    }

    amplitudeEventsLib.quickBookingPassengerSaved({
      offerId: props.offerId,
    });
    savePassengersForExtras().catch(() => {
      // Errors are surfaced via modals inside savePassengersForExtras.
    });
  };

  const openPassengerSheet = (key?: PassengerKey) => {
    const targetKey = key ?? passengerKeys.value[0] ?? null;
    selectedPassengerKey.value = targetKey;
    if (!targetKey) {
      return;
    }
    passengersSectionRef.value?.openSavedList(targetKey);
  };

  /** Snapshot the current fare selection into Pinia so it survives the
   *  QuickBookingPage remount that happens on back-navigation from the
   *  baggage/seat-map pages. */
  const persistFareSelection = () => {
    const ids: Record<string, string> = {};
    Object.entries(fareFamily.selectedFares).forEach(([groupKey, choice]) => {
      ids[groupKey] = choice.id;
    });
    bookingStore.setSelectedFareIds(
      props.offerId,
      Object.keys(ids).length > 0 ? ids : null,
    );
  };

  /** Re-apply a previously-persisted fare selection, looking each saved id
   *  back up among the freshly-loaded fare groups. Returns true when at least
   *  one fare was restored — preselect-first should then no-op so the user's
   *  prior choice isn't overwritten. */
  const restoreFareSelectionFromStore = (): boolean => {
    if (bookingStore.selectedFareIdsOfferId !== props.offerId) {
      return false;
    }
    const saved = bookingStore.selectedFareIds;
    if (!saved) {
      return false;
    }
    let restored = false;
    Object.entries(saved).forEach(([groupKey, fareId]) => {
      if (fareFamily.selectedFares[groupKey]) {
        return;
      }
      const choice = fareFamily.fareGroups.value[groupKey]?.find(
        c => c.id === fareId,
      );
      if (choice) {
        fareFamily.selectFare({ groupKey, choice });
        restored = true;
      }
    });
    return restored;
  };

  const scrollSelectedFareIntoView = (choiceId: string) => {
    nextTick(() => {
      const root = fareCarouselRef.value;
      if (!root) {
        return;
      }
      const el = root.querySelector<HTMLElement>(
        `[data-fare-id="${choiceId}"]`,
      );
      el?.scrollIntoView({
        inline: 'center',
        block: 'nearest',
        behavior: 'smooth',
      });
    });
  };

  const onFareCardSelect = async (
    groupKey: string,
    choice: FareFamilyChoiceSpec,
  ) => {
    // Re-tap on the already-selected fare is a no-op: avoids a wasted
    // changeFare round-trip and the carousel re-mount it triggers.
    if (fareFamily.selectedFares[groupKey]?.id === choice.id) {
      return;
    }
    fareFamily.selectFare({ groupKey, choice });
    persistFareSelection();
    if (Object.keys(fareFamily.fareGroups.value).length === 1) {
      details.changeFare(fareFamily.prepareFarePayload());
      // After the offer reload, <main> re-mounts and the carousel scrollLeft
      // resets to 0. Wait for the reload to finish, then center the picked
      // card so the user keeps sight of their choice.
      await waitForDetailsIdle();
      scrollSelectedFareIntoView(choice.id);
    }
  };

  // ── Per-segment (multi-direction) fare picker ──
  //
  // For RT / multi-leg offers we want the same UX the classic FareFamilyPage
  // provides: a card per leg + a bottom-sheet that compares choices for that
  // leg only. Selection waits until every leg has a fare picked, then commits
  // the combined payload to `details.changeFare` (which resets products
  // through onFareChange — see use-quick-booking).
  const fareFamilyGroupBottomSheet = useFareFamilyBottomSheet();
  const segmentGroupsMissingFares = ref<string[]>([]);

  const onOpenSegmentFareSelect = ({ groupKey }: { groupKey: string }) => {
    if (!details.dict.value) {
      return;
    }
    const flightGroup = fareFamily.flightGroups.value[groupKey];
    if (!flightGroup) {
      return;
    }
    fareFamilyGroupBottomSheet.show({
      groupKey,
      choices: fareFamily.fareGroups.value[groupKey] ?? [],
      selectedId: fareFamily.selectedFares[groupKey]?.id,
      isDomestic: bookingStore.offer?.$meta?.is_domestic,
      segments: {
        dep: flightGroup.segments[0].dep.city,
        arr: flightGroup.segments[flightGroup.segments.length - 1].arr.city,
      },
    });
  };

  const onSegmentFareSelect = (payload: {
    groupKey: string;
    choice: FareFamilyChoiceSpec;
  }) => {
    fareFamily.selectFare(payload);
    persistFareSelection();
    // Clear the red-border highlight when the user fills the offending slot.
    segmentGroupsMissingFares.value = segmentGroupsMissingFares.value.filter(
      key => key !== payload.groupKey,
    );
    // Fire the combined fare change only once all directions are picked.
    const groupKeys = Object.keys(fareFamily.fareGroups.value);
    const allFilled = groupKeys.every(key => fareFamily.selectedFares[key]);
    if (allFilled) {
      details.changeFare(fareFamily.prepareFarePayload());
    }
  };

  const onProceedToSeatMap = () => {
    updateProductsStateAtBookingStore();
    router.push({ name: 'SeatMapPage' });
  };

  const onProceedToBaggages = () => {
    updateProductsStateAtBookingStore();
    router.push({ name: 'AncillaryBaggagePage' });
  };

  const onSelectPackage = (selectedPackage: unknown) => {
    products.onPackageSelect(selectedPackage);
    updateProductsStateAtBookingStore();
  };

  const onUnselectPackage = () => {
    products.onPackageUnselect();
    updateProductsStateAtBookingStore();
  };

  const onPayClick = () => {
    if (!isAgreed.value) {
      submitErrorMessage.value = t('quickBooking.errors.agreementRequired');
      return;
    }
    // For multi-direction offers, highlight the legs that still need a fare.
    // The inline alert (driven by submitErrorMessage in onSubmit) covers the
    // top-level "select a fare" message; the per-leg red border comes from
    // segmentGroupsMissingFares which we update here before delegating.
    const groupKeys = Object.keys(fareFamily.fareGroups.value);
    if (groupKeys.length > 1) {
      segmentGroupsMissingFares.value = groupKeys.filter(
        key => !fareFamily.selectedFares[key],
      );
    }
    // Highlight passenger blocks that are not yet filled. Mirrors the
    // per-leg fare highlight above so users see exactly which rows still
    // need data — re-computed on every submit attempt so taps after the
    // first one keep the indicator in sync with the latest state.
    passengersMissingData.value = passengerKeys.value.filter(
      key => !isPassengerFilledFor(key),
    );
    amplitudeEventsLib.quickBookingPayClick({
      offerId: props.offerId,
      totalPrice: String(priceBreakdown.value.totalPrice ?? 0),
      ancillaryBaggageCount:
        Number(
          ancillariesStore.passengersWithBaggage.data?.passengers?.length ?? 0,
        ) || 0,
      hasSeats: !!bookingStore.state.seatMap,
      hasInsurance: bookingStore.medicalInsuranceProducts.length > 0,
    });
    onSubmit().catch(() => {
      // errors handled inside onSubmit via modals
    });
  };

  const onWarningConfirm = () => {
    onWarningConfirmFromModel();
  };

  const onCancelPrevBooking = () => {
    onCancelPrevBookingFromModel();
  };

  const goToExistingBooking = (orderId: unknown) => {
    goToExistingBookingFromModel(orderId);
  };

  const onBack = () => router.back();

  /** Map the passengers-schema keys (`ADT-0`, `CHD-1`, `INF-0`, `YTH-0`, ...)
   *  back into per-pax-type counts so they can be reported in analytics. */
  const computePaxBreakdown = () => {
    const keys = Object.keys(
      bookingStore.passengersSchema?.passengers?.schema ?? {},
    );
    return {
      adt: keys.filter(k => k.startsWith('ADT-')).length,
      chd: keys.filter(k => k.startsWith('CHD-')).length,
      inf: keys.filter(k => k.startsWith('INF-')).length,
      yth: keys.filter(k => k.startsWith('YTH-')).length,
    };
  };

  /** OW/RT derived from the loaded offer: `flights.length === 1` is one-way,
   *  >1 is a round-trip (multi-direction). Connecting flights (within a leg)
   *  are gated separately by eligibility. */
  const computeTripType = (): 'OW' | 'RT' =>
    (bookingStore.offer?.flights.length ?? 1) === 1 ? 'OW' : 'RT';

  // `flush: 'sync'` is required so the alert re-shows on repeat submit taps.
  // onSubmit resets submitErrorMessage to '' and then writes the same message
  // again synchronously; with the default async flush, Vue collapses the pair
  // into a no-op because the start/end values match and the watcher never
  // fires. Sync flush observes each assignment, so the second tap re-triggers
  // FreeAlert.show() and the user sees the toast again.
  watch(
    submitErrorMessage,
    value => {
      if (value) {
        invalidAlertRef.value?.show();
      }
    },
    { flush: 'sync' },
  );

  /** Resolves once the details composable's `isLoading` flag flips false —
   *  use to chain the next backend write (changeFare, save) behind any
   *  in-flight detail fetch instead of racing them. Function declaration so
   *  it hoists above the fare-select handlers that call it. */
  function waitForDetailsIdle(): Promise<void> {
    if (!details.isLoading.value) {
      return Promise.resolve();
    }
    return new Promise(resolve => {
      const stop = watch(
        () => details.isLoading.value,
        loading => {
          if (!loading) {
            stop();
            resolve();
          }
        },
      );
    });
  }

  /**
   * Auto-pick the first fare in each fare-family group when the user has not
   * yet chosen one. Returns true when at least one group was just selected
   * (so the caller knows it has to push the choice to the backend with
   * `details.changeFare`); returns false when nothing changed (e.g. user is
   * back-navigating with a fare already picked, or the offer has no fare
   * families at all).
   */
  const preselectFirstFareIfNeeded = (): boolean => {
    const groups = fareFamily.fareGroups.value;
    const groupKeys = Object.keys(groups);
    if (groupKeys.length === 0) {
      return false;
    }
    let didSelect = false;
    groupKeys.forEach(groupKey => {
      if (fareFamily.selectedFares[groupKey]) {
        return;
      }
      const choices = groups[groupKey];
      if (choices.length > 0) {
        fareFamily.selectFare({ groupKey, choice: choices[0] });
        didSelect = true;
      }
    });
    return (
      didSelect &&
      Object.keys(fareFamily.selectedFares).length === groupKeys.length
    );
  };

  /**
   * Initializes passengers from the schema and, when a saved passenger is
   * pre-selected (so the form is already filled), fires the same save call
   * that runs after the bottom-sheet closes. This way extras (products +
   * ancillaries) load up-front instead of after a manual "save" tap.
   * `savePassengersForExtras` itself guards against missing fields and against
   * re-saving an identical payload, so it's safe to call unconditionally.
   * Silent mode: an offer_expired response on this preflight should not
   * pre-empt the user — they will hit the same error explicitly on submit if
   * the offer is genuinely gone.
   *
   * Sequence: details → fare-change (if we just preselected) → save →
   * ancillaries. The save itself triggers an ancillaries init on success,
   * so we don't fetch ancillaries early at mount or inside the onFareChange
   * callback — that avoids racing them against the fare-change write and
   * also gives us personalised baggage data on the very first fetch.
   */
  const initPassengersAndSyncExtras = async () => {
    initPassengersFromSchema();

    // The schema-watch fires inside the details composable's `.then()` —
    // before its `.finally()` flips isLoading to false. Wait for that so
    // the next changeFare call doesn't overlap the still-running init.
    await waitForDetailsIdle();

    // First try to restore the user's previous fare choice (set in a prior
    // QuickBookingPage session for this same offer). If nothing was restored
    // — fresh entry into this offer — auto-pick the first fare in each group
    // and push the choice to the backend.
    const restored = restoreFareSelectionFromStore();
    if (!restored && preselectFirstFareIfNeeded()) {
      persistFareSelection();
      details.changeFare(fareFamily.prepareFarePayload());
      await waitForDetailsIdle();
    }

    // Fare is now finalized — kick off products + seatmap. Doing this here
    // (instead of synchronously at setup) ensures /airflow/products and
    // /airflow/seatmap see the fare the offer will actually be booked with.
    initProductsAfterFareReady();

    await savePassengersForExtras({ silent: true });
  };

  watch(
    () => bookingStore.passengersSchema,
    schema => {
      if (schema && !passengers.state.value) {
        initPassengersAndSyncExtras();
      }
    },
  );

  onMounted(() => {
    // Reset the booking store's per-offer state ONLY when we're entering a
    // different offer. The store is a Pinia singleton, so a navigation
    // A -> B would otherwise see A's stale schema/offer; but on a back-nav
    // from the baggage / seat-map pages to the SAME offer we want to keep
    // everything (selected fare in particular, which is reapplied below
    // from `bookingStore.selectedFareIds`).
    const isReturningToSameOffer =
      bookingStore.selectedFareIdsOfferId === props.offerId;
    const hasCachedOfferContext =
      isReturningToSameOffer &&
      Boolean(bookingStore.offer) &&
      Boolean(bookingStore.passengersSchema);

    if (!isReturningToSameOffer) {
      bookingStore.passengersSchema = null;
      bookingStore.offer = null;
      bookingStore.resetSelectedFareIds();
    }

    if (hasCachedOfferContext) {
      // Skip details.init() on return-nav from baggage/seat-map: it would
      // call bookingStore.init() -> update(defaultState), wiping
      // state.seatMap / state.products that the user just selected on the
      // sub-page. Drive the local pipeline directly from the cached schema.
      // The useDetails composable is freshly created with offer/dict refs at
      // null, so hydrate them from the cached bookingStore values —
      // otherwise useFareFamily.fareGroups stays empty and the fare picker
      // section disappears on return.
      details.offer.value = bookingStore.offer;
      details.dict.value = bookingStore.dict ?? null;
      initPassengersAndSyncExtras();
    } else {
      // Fresh entry (or different offer): re-fire details. `onInit`
      // atomically writes offer + schema, which kicks off the
      // schema-watch that drives preselect/restore + save. Ancillaries
      // load after the post-save callback: details -> fare-change ->
      // save -> ancillaries.
      details.init();
    }
  });

  /** Fire the page-open event once the offer is hydrated (so `$meta`,
   *  flight count, and passenger schema are all available) AND the products
   *  init has finished (so `preselectedPackageId` is settled). The watcher
   *  short-circuits after the first firing via `hasFiredPageOpenEvent`. */
  const hasFiredPageOpenEvent = ref(false);
  watch(
    [
      () => bookingStore.offer,
      () => bookingStore.passengersSchema,
      preselectedPackageId,
      isProductsInitDone,
    ],
    ([offer, schema, , productsDone]) => {
      if (hasFiredPageOpenEvent.value) {
        return;
      }
      if (!offer || !schema || !productsDone) {
        return;
      }
      hasFiredPageOpenEvent.value = true;
      const breakdown = computePaxBreakdown();
      amplitudeEventsLib.quickBookingPageOpen({
        offerId: props.offerId,
        searchQuery: (offer.$meta?.search_query as string) ?? '',
        providerClass: providerClass.value,
        hasWarnings: false,
        tripType: computeTripType(),
        paxCount: breakdown.adt + breakdown.chd + breakdown.inf + breakdown.yth,
        paxBreakdown: breakdown,
        isDomestic: Boolean(offer.$meta?.is_domestic),
        preselectedPackageId: preselectedPackageId.value,
      });
    },
    { immediate: true },
  );
</script>

<style scoped>
  /* PassengersSection is rendered only to host its nested bottom-sheets
     (saved-list, form, document-photo, etc.). Its own inline list UI
     would duplicate the QuickBooking passenger rows above, so we hide
     the header line and the multi-passenger v-for block here. The
     nested bottom-sheets stay reachable because they're separate
     children of the same root <section>. */
  .quick-booking-passengers-host :deep(section.space-y-3 > p),
  .quick-booking-passengers-host :deep(section.space-y-3 > section) {
    display: none;
  }

  /* Mirror the classic PassengerCard red-border treatment when a row's
     vuelidate state has errors. Inset box-shadow keeps the row flush
     inside the parent card (which uses `overflow-hidden rounded-2xl`)
     and avoids shifting siblings, while still drawing the eye like the
     `border-background-negative-primary` on PassengerCard does. */
  .quick-booking-passenger-row--invalid {
    box-shadow: inset 0 0 0 1px rgb(var(--background-negative-primary));
  }
</style>
